import { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';

import StatsCard from '../../../components/Cards/StartsCard';
import BarChart from '../../../components/Charts/BarChart';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import {
  Card,
  CardHead,
  CardTitle,
  Hint,
  Scroll,
  Segmented,
  Select,
  SimpleTable,
  Stack,
  StatsGrid,
  TwoColumn,
} from '../../../components/Layout/PagePieces';
import Page from '../../../components/Page/Page';
import usePurchasesAnalytics from '../../../hooks/dashboard/analytics/usePurchasesAnalytics';
import useTopDays from '../../../hooks/dashboard/analytics/useTopDays';
import useUsersAnalytics from '../../../hooks/dashboard/analytics/useUsersAnalytics';
import useYearsAnalytics from '../../../hooks/dashboard/analytics/useYearsAnalytics';
import { BILL_COLOURS, BILL_LABELS } from '../../../models/analytics/analytics.model';
import { formatNaira, formatNumber } from '../../../utils/utils';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const THIS_YEAR = new Date().getFullYear();

const pct = (value: number | null) =>
  value == null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;

// Growth charts (docs/ops-and-account-acceptance-criteria.md G, D25–D27).
// Every series is in Lagos time and has every empty bucket as 0.
const Analytics = () => {
  const [year, setYear] = useState(THIS_YEAR);
  const [month, setMonth] = useState<number | null>(null);
  const [usersView, setUsersView] = useState<'chart' | 'table'>('chart');
  const [salesView, setSalesView] = useState<'chart' | 'table'>('chart');
  const { users, fetchYear, fetchMonth } = useUsersAnalytics();
  const { topDays, fetchTopDays } = useTopDays();
  const { purchases, fetchPurchases } = usePurchasesAnalytics();
  const { years, fetchYears } = useYearsAnalytics();

  useEffect(() => {
    fetchYears();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (month) fetchMonth(`${year}-${String(month).padStart(2, '0')}`);
    else fetchYear(year);
    // eslint-disable-next-line
  }, [year, month]);

  useEffect(() => {
    fetchPurchases(year);
    const to = year === THIS_YEAR ? new Date().toISOString().slice(0, 10) : `${year}-12-31`;
    fetchTopDays(`${year}-01-01`, to);
    // eslint-disable-next-line
  }, [year]);

  const yearOptions = useMemo(() => {
    const list = years.map((y) => y.year);
    return list.length ? list.sort((a, b) => b - a) : [THIS_YEAR];
  }, [years]);

  const salesSeries = (purchases?.series ?? []).map((m) => ({
    label: m.label,
    ...Object.fromEntries(Object.keys(BILL_LABELS).map((k) => [k, Number(m.by_bill_type[k] ?? 0)])),
    count: m.count,
    value: Number(m.value),
  }));
  const userSeries = (users?.series ?? []).map((p) => ({ ...p }));

  return (
    <Page title='Hethera | Analytics'>
      <PageLayout>
        <Stack>
          <PageHeader
            title='Analytics'
            className='borderless no-bottom-padding'
            customComp={
              <Select
                aria-label='Year'
                value={year}
                onChange={(e) => {
                  setYear(Number(e.target.value));
                  setMonth(null);
                }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            }
          />

          <StatsGrid>
            <StatsCard.Root>
              <StatsCard.Header>Onboarded in {month ? `${MONTHS[month - 1]} ` : ''}{year}</StatsCard.Header>
              <StatsCard.Value>{formatNumber(users?.total_onboarded ?? 0)}</StatsCard.Value>
              <StatsCard.PlainFooter>{formatNumber(users?.total_new ?? 0)} sent a first message</StatsCard.PlainFooter>
            </StatsCard.Root>
            <StatsCard.Root>
              <StatsCard.Header>Busiest month</StatsCard.Header>
              <StatsCard.Value>{purchases?.busiest_month?.label ?? '—'}</StatsCard.Value>
              <StatsCard.PlainFooter>
                {purchases?.busiest_month ? `${formatNumber(purchases.busiest_month.count)} purchases` : 'No purchases yet'}
              </StatsCard.PlainFooter>
            </StatsCard.Root>
            <StatsCard.Root>
              <StatsCard.Header>Busiest day</StatsCard.Header>
              <StatsCard.Value>{purchases?.busiest_day ? `${purchases.busiest_day.weekday} ${purchases.busiest_day.date.slice(5)}` : '—'}</StatsCard.Value>
              <StatsCard.PlainFooter>
                {purchases?.busiest_day ? `${formatNumber(purchases.busiest_day.count)} purchases, ${formatNaira(Number(purchases.busiest_day.value))}` : ' '}
              </StatsCard.PlainFooter>
            </StatsCard.Root>
            <StatsCard.Root>
              <StatsCard.Header>Sold most</StatsCard.Header>
              <StatsCard.Value>{purchases?.top_bill_type ? BILL_LABELS[purchases.top_bill_type.key] ?? purchases.top_bill_type.key : '—'}</StatsCard.Value>
              <StatsCard.PlainFooter>
                {purchases?.top_provider ? `Top provider: ${purchases.top_provider.key} (${formatNumber(purchases.top_provider.count)})` : ' '}
              </StatsCard.PlainFooter>
            </StatsCard.Root>
          </StatsGrid>

          <Card>
            <CardHead>
              <div>
                <CardTitle>{month ? `New users in ${MONTHS[month - 1]} ${year}, by day` : `User growth in ${year}, by month`}</CardTitle>
                <Hint>Registered = sent a first message. Onboarded = finished set-up.</Hint>
              </div>
              <Segmented>
                <button className={usersView === 'chart' ? 'active' : ''} onClick={() => setUsersView('chart')}>Chart</button>
                <button className={usersView === 'table' ? 'active' : ''} onClick={() => setUsersView('table')}>Table</button>
              </Segmented>
            </CardHead>
            <Segmented aria-label='Pick a month to see its days'>
              <button className={month === null ? 'active' : ''} onClick={() => setMonth(null)}>Whole year</button>
              {MONTHS.map((name, i) => (
                <button key={name} className={month === i + 1 ? 'active' : ''} onClick={() => setMonth(i + 1)}>
                  {name}
                </button>
              ))}
            </Segmented>
            {usersView === 'chart' ? (
              <BarChart
                data={userSeries}
                dataKeys={['new_users', 'onboarded']}
                xAxisDataKey='label'
                fills={['#C9D6CC', '#1F7A45']}
                tooltipLabel={['Registered', 'Onboarded']}
                aspect={month ? 3.2 : 2.6}
              />
            ) : (
              <Scroll>
                <SimpleTable>
                  <thead>
                    <tr>
                      <th>{month ? 'Day' : 'Month'}</th>
                      <th className='num'>Registered</th>
                      <th className='num'>Onboarded</th>
                      {!month && <th className='num'>Onboarded so far</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {userSeries.map((p) => (
                      <tr key={p.period}>
                        <td>{p.label}</td>
                        <td className='num'>{formatNumber(p.new_users)}</td>
                        <td className='num'>{formatNumber(p.onboarded)}</td>
                        {!month && <td className='num'>{formatNumber(p.cumulative_onboarded ?? 0)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </SimpleTable>
              </Scroll>
            )}
          </Card>

          <TwoColumn>
            <Card>
              <CardTitle>Best onboarding days in {year}</CardTitle>
              <SimpleTable>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th className='num'>Onboarded</th>
                  </tr>
                </thead>
                <tbody>
                  {(topDays?.records ?? []).map((r) => (
                    <tr key={r.date}>
                      <td>{r.rank}</td>
                      <td>
                        {r.weekday} {r.date}
                      </td>
                      <td className='num'>{formatNumber(r.onboarded)}</td>
                    </tr>
                  ))}
                  {!topDays?.records?.length && (
                    <tr>
                      <td colSpan={3}>No one onboarded in this period yet.</td>
                    </tr>
                  )}
                </tbody>
              </SimpleTable>
            </Card>
            <Card>
              <CardTitle>Average onboarded per weekday</CardTitle>
              <BarChart
                data={(topDays?.weekdays ?? []).map((w) => ({ label: w.weekday, average: w.average }))}
                dataKeys={['average']}
                xAxisDataKey='label'
                fills={['#1F7A45']}
                tooltipLabel={['Average onboarded']}
                showLegend={false}
                aspect={1.8}
              />
            </Card>
          </TwoColumn>

          <Card>
            <CardHead>
              <div>
                <CardTitle>Purchases in {year}, by month</CardTitle>
                <Hint>Successful bill purchases (not gift funding). Value is the bill; service fees are separate.</Hint>
              </div>
              <Segmented>
                <button className={salesView === 'chart' ? 'active' : ''} onClick={() => setSalesView('chart')}>Chart</button>
                <button className={salesView === 'table' ? 'active' : ''} onClick={() => setSalesView('table')}>Table</button>
              </Segmented>
            </CardHead>
            {salesView === 'chart' ? (
              <BarChart
                data={salesSeries}
                dataKeys={Object.keys(BILL_LABELS)}
                xAxisDataKey='label'
                fills={Object.keys(BILL_LABELS).map((k) => BILL_COLOURS[k])}
                tooltipLabel={Object.values(BILL_LABELS)}
                aspect={2.6}
              />
            ) : (
              <Scroll>
                <SimpleTable>
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className='num'>Purchases</th>
                      <th className='num'>Value</th>
                      {Object.values(BILL_LABELS).map((label) => (
                        <th key={label} className='num'>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(purchases?.series ?? []).map((m) => (
                      <tr key={m.period}>
                        <td>{m.label}</td>
                        <td className='num'>{formatNumber(m.count)}</td>
                        <td className='num'>{formatNaira(Number(m.value))}</td>
                        {Object.keys(BILL_LABELS).map((k) => (
                          <td key={k} className='num'>
                            {formatNaira(Number(m.by_bill_type[k] ?? 0))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </SimpleTable>
              </Scroll>
            )}
            <TwoColumn>
              <Ranking title='By bill type' rows={(purchases?.by_bill_type ?? []).map((r) => ({ ...r, key: BILL_LABELS[r.key] ?? r.key }))} />
              <Ranking title='By provider' rows={purchases?.by_provider ?? []} />
            </TwoColumn>
          </Card>

          <Card>
            <CardTitle>Year on year</CardTitle>
            <YearGrid>
              {years.map((y) => {
                const peak = Math.max(1, ...y.monthly_purchases);
                return (
                  <YearCard key={y.year} onClick={() => setYear(y.year)} className={y.year === year ? 'active' : ''}>
                    <strong>{y.year}</strong>
                    <span>
                      {formatNumber(y.onboarded)} onboarded <em>{pct(y.onboarded_change_pct)}</em>
                    </span>
                    <span>
                      {formatNumber(y.purchases)} purchases <em>{pct(y.purchases_change_pct)}</em>
                    </span>
                    <span>
                      {formatNaira(Number(y.value))} <em>{pct(y.value_change_pct)}</em>
                    </span>
                    <Spark aria-label={`Purchases by month in ${y.year}`}>
                      {y.monthly_purchases.map((n, i) => (
                        <i key={i} title={`${MONTHS[i]}: ${n}`} style={{ height: `${Math.max(4, (n / peak) * 100)}%` }} />
                      ))}
                    </Spark>
                  </YearCard>
                );
              })}
            </YearGrid>
          </Card>
        </Stack>
      </PageLayout>
    </Page>
  );
};

export default Analytics;

const Ranking: React.FC<{ title: string; rows: { key: string; count: number; value: string }[] }> = ({ title, rows }) => (
  <SimpleTable>
    <thead>
      <tr>
        <th>{title}</th>
        <th className='num'>Purchases</th>
        <th className='num'>Value</th>
      </tr>
    </thead>
    <tbody>
      {rows.slice(0, 8).map((r) => (
        <tr key={r.key}>
          <td>{r.key}</td>
          <td className='num'>{formatNumber(r.count)}</td>
          <td className='num'>{formatNaira(Number(r.value))}</td>
        </tr>
      ))}
      {!rows.length && (
        <tr>
          <td colSpan={3}>None yet.</td>
        </tr>
      )}
    </tbody>
  </SimpleTable>
);

const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

const YearCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 16px;
  padding: 16px;
  background: white;
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  &.active {
    border-color: ${({ theme }) => theme.palette.HetheraBlack};
  }

  strong {
    font-size: 18px;
  }

  em {
    font-style: normal;
    color: ${({ theme }) => theme.palette.grayTextColor};
  }
`;

const Spark = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  align-items: end;
  gap: 2px;
  height: 40px;
  margin-top: 6px;

  i {
    display: block;
    background: #1f7a45;
    border-radius: 2px;
  }
`;
